import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Space, Form, message } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import 'components/order/Order.styles.css';
import moment from 'moment';
import { OrderModel } from '@app/interfaces/order';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { addProductItems, createOrder, getOrder, updateOrder } from '@app/store/slices/orderSlice';
import { useNavigate } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import PrintOrderReturnTemp from './detail/PrintOrderReturnTemp';
import { useAppSetting } from '@app/contexts/AppSettingContext';
import OrderProductItems from '../order/createUpdate/OrderProductItems';
import { TbTruckReturn } from 'react-icons/tb';
import { ORDER_STATUS_OPEN } from '@app/constants/order';
import { DEPARTMENT_DESIGNER, DEPARTMENT_SHIPPER, EmployeeOption } from '@app/interfaces/employee';
import { StoreModel, StoreOption } from '@app/interfaces/store';
import { getStores } from '@app/store/slices/storeSlice';
import { getListEmployees } from '@app/api/order.api';
import OrderCustomerForm from '../order/createUpdate/OrderCustomerForm';
import { MdCancel } from 'react-icons/md';
import { printHtmlBody } from '@app/utils/printDocument';

interface OrderFormProps {
  tabKey: string;
  orderId?: string | null;
  record?: OrderModel | null;
}

interface CustomerState {
  id?: number;
  name?: string;
  address?: string;
}

const OrderReturnForm: React.FC<OrderFormProps> = ({ tabKey, orderId, record }) => {
  const { logoUrl, storeName, storePhone, storeAddress } = useAppSetting();
  const [form] = Form.useForm();
  const [customer, setCustomer] = useState<CustomerState>({});
  const [orderRecord, setOrderRecord] = useState<OrderModel | null>(null);
  const [sellerOptions, setSellerOptions] = useState<EmployeeOption[]>([]);
  const [shipperOptions, setShipperOptions] = useState<EmployeeOption[]>([]);
  const [designerOptions, setDesignerOptions] = useState<EmployeeOption[]>([]);
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
  const [, setStore] = useState<StoreModel | null>(null);
  const [, setTotalAmountTemp] = useState(0);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
    fetchStores();
  }, []);

  useEffect(() => {
    if (orderId) {
      fetchOrder(orderId);
    }
  }, [orderId]);

  useEffect(() => {
    if (orderRecord) {
      const formValues = {
        ...orderRecord,
        trans_date: moment(),
        delivery_date: moment(),
        before_discount_amount: orderRecord.before_discount_amount * -1,
        total_amount: orderRecord.total_amount * -1
      };
      form.setFieldsValue(formValues);
      dispatch(addProductItems(orderRecord.items));

      setCustomer({
        id: orderRecord.customer?.id,
        name: orderRecord.customer?.name,
        address: orderRecord.customer?.address,
      });
    }
  }, [orderRecord, form, dispatch]);

  useEffect(() => {
    if (record) {
      const formValues = {
        ...record,
        trans_date: record.trans_date ? moment(record.trans_date) : moment(),
        delivery_date: record.delivery_date ? moment(record.delivery_date) : moment()
      };
      form.setFieldsValue(formValues);
      dispatch(addProductItems(record.items));
    }
  }, [record, form, dispatch]);

  const fetchStores = async () => {
    try {
      const response = await dispatch(getStores());
      const data = response.payload as StoreModel[];
      if (data && data?.length > 0) {
        setStore(data[0]);

        const options = data.map((item: any) => ({ value: item.id, label: item.name }));
        setStoreOptions(options);
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const fetchOrder = async (orderId: string) => {
    try {
      const response = await dispatch(getOrder(Number(orderId))).unwrap();
      if (response) {
        setOrderRecord(response);
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await getListEmployees();

      const groupedData = response?.reduce(
        (acc: any, item: any) => {
          const { department } = item;
          if (department === DEPARTMENT_SHIPPER) {
            acc.shipper.push(item);
          } else if (department === DEPARTMENT_DESIGNER) {
            acc.designer.push(item);
          } else {
            acc.seller.push(item);
          }
          return acc;
        },
        { shipper: [], designer: [], seller: [] }, // Initial accumulator
      );

      // Giữ cùng shape với OrderForm: OrderCustomerForm cần avatar_url và
      // employee_code để render avatar + mã nhân viên trong từng lựa chọn.
      const mapToOptions = (data: any) =>
        data?.map((item: any) => ({
          value: item.id,
          label: item.name,
          avatar_url: item.avatar_url,
          employee_code: item.employee_code,
          department: item.department,
        }));

      setSellerOptions(mapToOptions(groupedData.seller));
      setShipperOptions(mapToOptions(groupedData.shipper));
      setDesignerOptions(mapToOptions(groupedData.designer));
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleSubmit = (action: string) => {
    form
      .validateFields()
      .then((values) => {
        //form.resetFields();

        values = {
          ...values,
          discount_amount:
            values.discount_percent > 0 ? (values.discount_percent / 100) * values.before_discount_amount : 0,
          order_code: record?.order_code,
          seller: record?.seller_id,
          source_order_id: record?.id,
          order_type: 'return',
          order_status_id: ORDER_STATUS_OPEN,
        };
        action === 'create_update' ? onCreate(values) : handlePrint(values, customer);
      })
      .catch((info) => {
        message.error('Validate failed:', info);
      });
  };

  const onCreate = async (values: any) => {
    try {
      let response = null;

      if(record && record.id) {
        response = await dispatch(updateOrder({ orderId: Number(record.id), formData: values })).unwrap();
      } else {
        const formData = new FormData();

        Object.keys(values).forEach(key => {
          formData.append(key, values[key]);
        });

        formData.append('remark', values.remark || '');
        if (values.items) {
          formData.append('items', JSON.stringify(values.items));
        }
        response = await dispatch(createOrder(formData)).unwrap();
      }

      if (response && response.data && response.data.id) {
        navigate(`/order-returns/${response.data.id}`);
      } else {
        throw new Error('Order return creation failed. Please try again.');
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handlePrint = async (values: any, customer: any) => {
    try {
      const absoluteLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${window.location.origin}${logoUrl}`;
      const printContent = ReactDOMServer.renderToString(
        <PrintOrderReturnTemp
          record={values}
          customer={customer}
          logoUrl={absoluteLogoUrl}
          storeName={storeName}
          storePhone={storePhone}
          storeAddress={storeAddress}
        />,
      );
      await printHtmlBody(printContent, { title: 'In đơn hàng tạm' });
    } catch (error) {
      message.error('An error occurred while printing.');
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const totalOriginalOrder = orderRecord?.total_amount || 0;

  return (
    <Form
      form={form}
      name="form-order"
      initialValues={{
        //seller: user?.name,
        trans_date: moment(),
        order_channel_id: 1,
        payment_type: 'cash',
        store_id: 1,
        delivery_date: moment().add(6, 'hours'), //add 6 hours
      }}
      className="order-container"
    >
      <Row gutter={[16, 16]}>
        <Col span={14} className="tw-flex tw-h-full tw-flex-col tw-gap-4">
          <OrderProductItems form={form} onCalcTotal={() => { return; }} />
        </Col>
        <Col span={10} className="tw-h-full">
          <OrderCustomerForm
            form={form}
            setCustomer={setCustomer}
            sellerOptions={sellerOptions}
            designerOptions={designerOptions}
            shipperOptions={shipperOptions}
            storeOptions={storeOptions}
            setTotalAmount={setTotalAmountTemp}
            totalOriginalOrder={totalOriginalOrder}
            returnOrder={true}
          />
        </Col>
      </Row>

      <div className="order-button">
        <Space>
          <Button type="dashed" onClick={() => handleSubmit('print')}>
            <PrinterOutlined /> In
          </Button>
          <Button type="primary" onClick={() => handleSubmit('create_update')}>
            <TbTruckReturn /> {record ? 'Cập nhật' : 'Trả hàng'}
          </Button>
          <Button type="primary" danger onClick={handleCancel} style={{ marginLeft: 10 }}>
            <MdCancel /> Huỷ
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default OrderReturnForm;
